import { useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { submitOnRegistrationSurvey } from "../api/onRegistrationSurvey";
import { OnRegistrationSurveyRequest } from "../types/survey";

/**
 * Custom hook for on registration survey submission
 *
 * Provides survey submission functionality with loading states and error handling
 * following the same patterns as useAuth hook.
 */
export const useOnRegistrationSurvey = () => {
  const { user } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Submits the registration survey data
   *
   * @param surveyData - The survey form data to submit
   * @returns Promise that resolves when submission is complete
   */
  const submitSurvey = async (
    surveyData: OnRegistrationSurveyRequest
  ): Promise<void> => {
    if (!user?._id) {
      throw new Error("User not authenticated");
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitOnRegistrationSurvey(user._id, surveyData);
    } catch (err: any) {
      console.error("Survey submission failed:", err);
      setError(err.message || "Failed to submit survey. Please try again.");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitSurvey,
    isSubmitting,
    error,
  };
};
